import React, { useEffect, useMemo } from 'react'
import { useParams } from 'react-router-dom'
import { useDispatch, useSelector } from 'react-redux'
import { useForm, FormProvider } from 'react-hook-form'
import clsx from 'clsx'
import {
  EndJourneyResponses,
  Question,
  RespondentInfo,
} from '../../types/end-journey'
import { InfoQuestion } from './components/questions/InfoQuestion'
import { TextAreaQuestion } from './components/questions/TextAreaQuestion'
import { MultiSelectQuestion } from './components/questions/MultiSelectQuestion'
import { RatingQuestion } from './components/questions/RatingQuestion'
import { Intro } from './components/Intro'
import { CompletionView } from './components/CompletionView'
import { Header } from './components/Header'
import { RootState, AppDispatch } from '../../app/store'
import {
  nextQuestion,
  previousQuestion,
  setResponse,
  goToQuestion,
  completeAssessment,
  fetchAssessmentData,
  fetchResponses,
  submitResponse,
} from './store/endJourneySlice'
import { useToast } from '@chakra-ui/react'
import { RespondentGroupsQuestion } from './components/questions/RespondentGroupsQuestion'
import { ListQuestion } from './components/questions/ListQuestion'
import { useLocale } from '../../hooks/useLocale'
import { DEFAULT_FEEDBACK_REQUEST_LIMITS } from '../../utils/feedbackRequest'

type FormValues = {
  [key: string]: string | string[] | number
}

export const EndJourneyAssessment: React.FC = () => {
  const { userId } = useParams<{ userId: string }>()
  const dispatch = useDispatch<AppDispatch>()
  const { t } = useLocale()
  const {
    sessionId,
    user,
    intro,
    currentQuestionIndex,
    questions,
    responses,
    isComplete,
    isLoading,
    error,
  } = useSelector((state: RootState) => state.endJourney)

  const groups = useMemo(() => {
    if (!user) return []
    const limits = user.feedbackRequest ?? DEFAULT_FEEDBACK_REQUEST_LIMITS
    return [
      {
        title: 'Manager(s)',
        type: 'manager' as const,
        emails: user.managers || [],
        minRequired: limits.manager?.min,
      },
      {
        title: 'Peer(s)',
        type: 'peer' as const,
        emails: user.peers || [],
        minRequired: limits.peer?.min,
      },
      {
        title: 'Direct report(s)',
        type: 'reportee' as const,
        emails: user.reportees || [],
        minRequired: limits.reportee?.min,
      },
    ]
  }, [user])

  const formMethods = useForm<FormValues>({
    defaultValues:
      (responses as EndJourneyResponses)?.answers?.reduce(
        (acc: FormValues, response: EndJourneyResponses['answers'][number]) => {
          const questionId = response.questionId
          const answer = response.answer
          const otherAnswer = response.otherAnswer
          const leadershipValue = response.leadershipValue

          return {
            ...acc,
            [questionId]: answer,
            ...(otherAnswer && { [`${questionId}_other`]: otherAnswer }),
            ...(leadershipValue && {
              [`${questionId}_leadershipValue`]: leadershipValue,
            }),
          }
        },
        {}
      ) || {},
  })

  const currentQuestion = questions[currentQuestionIndex]
  const isLastQuestion = currentQuestionIndex === questions.length - 1
  const isIntroView = currentQuestionIndex === -1

  const toast = useToast()

  useEffect(() => {
    if (userId) {
      dispatch(fetchAssessmentData(userId))
      dispatch(fetchResponses(userId))
    }
  }, [dispatch, userId])

  useEffect(() => {
    if (error) {
      toast({
        position: 'top',
        title: 'Error',
        description: error,
        status: 'error',
        duration: 5000,
        isClosable: true,
      })
    }
  }, [error, toast])

  const handleResponse = async (
    questionId: string,
    answer: string | string[],
    isLastQuestion: boolean,
    otherAnswer?: string,
    leadershipValue?: string
  ) => {
    if (userId) {
      await dispatch(
        submitResponse({
          userId,
          response: {
            sessionId,
            questionId,
            answer,
            otherAnswer,
            isLastQuestion,
            leadershipValue,
          },
        })
      )
    }
    dispatch(
      setResponse({
        questionId,
        answer,
        otherAnswer,
        isLastQuestion,
        leadershipValue,
      })
    )
  }

  const handleNext = async () => {
    if (currentQuestion.type === 'info' || currentQuestion.type === 'list') {
      if (isLastQuestion) {
        dispatch(completeAssessment())
      } else {
        dispatch(nextQuestion())
      }
      return
    }

    const triggerId =
      currentQuestion.type === 'textarea' ||
      currentQuestion.type === 'feedback-emails'
        ? `${currentQuestion._id}_other`
        : currentQuestion._id
    const result = await formMethods.trigger(triggerId)

    if (!result) return

    if (currentQuestion.type === 'feedback-emails') {
      const nominated = (formMethods.getValues(triggerId) ??
        {}) as unknown as Record<string, RespondentInfo[] | undefined>
      const short = groups.find(
        (group) =>
          (nominated[group.type] ?? []).filter((r) => r.email?.trim()).length <
          (group.minRequired ?? 0)
      )
      if (short) {
        toast({
          position: 'top',
          title: 'Error',
          description: t('min_type_emails_required', {
            min: short.minRequired,
            type: short.type,
          }),
          status: 'error',
          duration: 5000,
          isClosable: true,
        })
        return
      }
    }

    // Get the value from the appropriate field
    const value = formMethods.getValues(currentQuestion._id)
    const otherAnswer = formMethods.getValues(`${currentQuestion._id}_other`)
    const leadershipValue = formMethods.getValues(
      `${currentQuestion._id}_leadershipValue`
    )

    await handleResponse(
      currentQuestion._id,
      Array.isArray(value) ? (value as string[]) : value?.toString(),
      isLastQuestion,
      typeof otherAnswer === 'object'
        ? JSON.stringify(otherAnswer)
        : otherAnswer?.toString(),
      leadershipValue?.toString()
    )

    if (isLastQuestion) {
      dispatch(completeAssessment())
      return
    }

    dispatch(nextQuestion())
  }

  const handleBack = () => {
    dispatch(previousQuestion())
  }

  const handleStart = () => {
    dispatch(goToQuestion(0))
  }


  const renderQuestion = (question: Question) => {
    switch (question.type) {
      case 'info':
        return <InfoQuestion key={question._id} question={question} />

      case 'textarea':
        return (
          <TextAreaQuestion
            key={question._id}
            name={question._id}
            question={question}
          />
        )

      case 'multiselect':
        return (
          <MultiSelectQuestion
            key={question._id}
            name={question._id}
            question={question}
          />
        )

      case 'list':
        return <ListQuestion key={question._id} question={question} />

      case 'value-score':
        return (
          <RatingQuestion
            key={question._id}
            name={question._id}
            question={question}
            userCompany={user?.company}
          />
        )

      case 'feedback-emails':
        return (
          <RespondentGroupsQuestion
            key={question._id}
            name={question._id}
            question={question}
            groups={groups}
          />
        )

      default:
        return null
    }
  }

  if (isLoading || !intro) {
    return (
      <div className="min-h-screen flex flex-col bg-[#EDF2F6]">
        <Header />
        <div className="flex-1 flex items-center justify-center">
          <div className="text-center">
            <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary mx-auto"></div>
            <p className="mt-4 text-gray-600">Loading assessment...</p>
          </div>
        </div>
      </div>
    )
  }

  // If assessment is complete, show completion view
  if (isComplete) {
    return (
      <div className="min-h-screen flex flex-col bg-[#EDF2F6]">
        <Header />
        <div className="flex-1 mt-6 sm:mt-10">
          <div className="max-w-4xl mx-auto px-4 sm:px-6">
            <CompletionView />
          </div>
        </div>
      </div>
    )
  }

  return (
    <div className="min-h-screen flex flex-col bg-[#EDF2F6]">
      <Header />

      <div className="flex-1 mt-6 sm:mt-10">
        <div className="max-w-4xl mx-auto px-4 sm:px-6">
          <FormProvider {...formMethods}>
            <div className="bg-white rounded-lg shadow-sm overflow-hidden">
              {!isIntroView ? (
                <div>
                  {/* Progress bar */}
                  <div className="h-1 bg-gray-100">
                    <div
                      className="h-full bg-blue-500 transition-all duration-300"
                      style={{
                        width: `${
                          ((currentQuestionIndex + 1) / questions.length) * 100
                        }%`,
                      }}
                    />
                  </div>

                  {/* Question content */}
                  {renderQuestion(currentQuestion)}

                  {/* Navigation buttons */}
                  <div className="flex flex-col sm:flex-row justify-center gap-3 sm:gap-4 py-6 sm:py-8 px-4 sm:px-6">
                    <button
                      onClick={handleBack}
                      disabled={currentQuestionIndex === 0}
                      className={clsx(
                        'px-4 sm:px-6 py-2 min-w-32 sm:min-w-52 rounded-full font-medium text-sm sm:text-base text-gray-600',
                        currentQuestionIndex === 0
                          ? 'opacity-50 cursor-not-allowed'
                          : 'hover:bg-gray-50'
                      )}
                    >
                      Go back
                    </button>
                    <button
                      onClick={handleNext}
                      className="px-4 sm:px-6 py-2 min-w-32 sm:min-w-52 rounded-full font-medium text-sm sm:text-base bg-primary text-white hover:bg-primary-600"
                    >
                      {isLastQuestion ? 'Complete' : 'Next'}
                    </button>
                  </div>
                </div>
              ) : (
                <Intro intro={intro} onStart={handleStart} />
              )}
            </div>
          </FormProvider>
        </div>
      </div>
    </div>
  )
}
